import React from 'react';
import styled from 'styled-components';
import { 
  Mail, 
  MessageCircle, 
  BookOpen, 
  Phone, 
  ExternalLink, 
  Clock,
  HelpCircle,
  FileText,
  Video,
  Users,
  Zap,
  ArrowRight
} from 'lucide-react';
import { theme } from '../styles/theme';
import { BentoGridItem } from './BentoGrid';

const SupportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const ContactCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
  transition: all ${theme.transitions.base};

  &:hover {
    border-color: ${theme.colors.primary[300]};
    box-shadow: ${theme.shadows.md};
  }
`;

const ContactHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const ContactIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary[100]};
  color: ${theme.colors.primary[600]};
`;

const ContactTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const ContactDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing[4]};
  line-height: ${theme.typography.lineHeight.normal};
`;

const ContactButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.primary[700]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[100]};
    border-color: ${theme.colors.primary[300]};
    color: ${theme.colors.primary[800]};
    transform: translateY(-1px);
  }

  svg {
    color: ${theme.colors.primary[500]};
  }
`;

const QuickLinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
`;

const QuickLinkCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    border-color: ${theme.colors.primary[300]};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
`;

const QuickLinkHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[3]};
`;

const QuickLinkIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary[100]};
  color: ${theme.colors.primary[600]};
`;

const QuickLinkTitle = styled.h4`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const QuickLinkDescription = styled.p`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textSecondary};
  margin: 0;
  line-height: ${theme.typography.lineHeight.normal};
`;

const SupportHours = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border};
`;

const HoursTitle = styled.h4`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[3]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const HoursList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const HoursItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${theme.typography.fontSize.xs};
`;

const HoursDay = styled.span`
  color: ${theme.colors.textSecondary};
`;

const HoursTime = styled.span`
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const SupportContactCard: React.FC = () => {
  const contactMethods = [
    {
      icon: <Mail size={20} />,
      title: 'Email Support',
      description: 'Get detailed help and support via email. We typically respond within 2-4 hours.',
      action: 'Send Email',
      link: 'mailto:support@opentender.ai'
    },
    {
      icon: <MessageCircle size={20} />,
      title: 'Live Chat',
      description: 'Chat with our support team in real-time for immediate assistance.',
      action: 'Start Chat',
      link: '#'
    },
    {
      icon: <Phone size={20} />,
      title: 'Schedule Call',
      description: 'Book a one-on-one session with our specialists for personalized guidance.',
      action: 'Book Call',
      link: '#'
    }
  ];

  const quickLinks = [
    {
      icon: <BookOpen size={16} />,
      title: 'Documentation',
      description: 'Comprehensive guides and tutorials',
      link: '#'
    },
    {
      icon: <Video size={16} />,
      title: 'Video Tutorials',
      description: 'Step-by-step visual guides',
      link: '#'
    },
    {
      icon: <HelpCircle size={16} />,
      title: 'FAQ',
      description: 'Common questions answered',
      link: '#'
    },
    {
      icon: <FileText size={16} />,
      title: 'Release Notes',
      description: 'Latest updates and features',
      link: '#'
    },
    {
      icon: <Users size={16} />,
      title: 'Community',
      description: 'Connect with other users',
      link: '#'
    },
    {
      icon: <Zap size={16} />,
      title: 'Feature Requests',
      description: 'Suggest new features',
      link: '#'
    }
  ];

  const supportHours = [
    { day: 'Monday - Friday', time: '9:00 AM - 6:00 PM PST' },
    { day: 'Saturday', time: '10:00 AM - 4:00 PM PST' },
    { day: 'Sunday', time: 'Closed' },
    { day: 'Emergency Support', time: '24/7 for Enterprise' }
  ];

  const handleContactClick = (link: string) => {
    if (link.startsWith('mailto:')) {
      window.location.href = link;
    } else if (link !== '#') {
      window.open(link, '_blank');
    } else {
      // Placeholder for actual functionality
      console.log('Contact method clicked');
    }
  };

  const handleQuickLinkClick = (link: string) => {
    if (link !== '#') {
      window.open(link, '_blank');
    } else {
      // Placeholder for actual functionality
      console.log('Quick link clicked');
    }
  };

  return (
    <SupportContainer>
      {/* Main Support Card */}
      <BentoGridItem
        title="Get Help & Support"
        description="We're here to help you succeed with OpenTender AI"
        icon={<HelpCircle size={24} />}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
          {contactMethods.map((method, index) => (
            <ContactCard key={index}>
              <ContactHeader>
                <ContactIcon>{method.icon}</ContactIcon>
                <ContactTitle>{method.title}</ContactTitle>
              </ContactHeader>
              <ContactDescription>{method.description}</ContactDescription>
              <ContactButton onClick={() => handleContactClick(method.link)}>
                {method.action}
                <ArrowRight size={16} />
              </ContactButton>
            </ContactCard>
          ))}
        </div>
      </BentoGridItem>

      {/* Quick Links */}
      <BentoGridItem
        title="Quick Help"
        description="Find answers and resources quickly"
        icon={<Zap size={24} />}
      >
        <QuickLinksGrid>
          {quickLinks.map((link, index) => (
            <QuickLinkCard key={index} onClick={() => handleQuickLinkClick(link.link)}>
              <QuickLinkHeader>
                <QuickLinkIcon>{link.icon}</QuickLinkIcon>
                <ExternalLink size={14} color={theme.colors.textMuted} />
              </QuickLinkHeader>
              <QuickLinkTitle>{link.title}</QuickLinkTitle>
              <QuickLinkDescription>{link.description}</QuickLinkDescription>
            </QuickLinkCard>
          ))}
        </QuickLinksGrid>
      </BentoGridItem>

      {/* Support Hours */}
      <BentoGridItem
        title="Support Hours"
        description="When you can reach our team"
        icon={<Clock size={24} />}
      >
        <SupportHours>
          <HoursTitle>
            <Clock size={16} />
            Available Hours
          </HoursTitle>
          <HoursList>
            {supportHours.map((hours, index) => (
              <HoursItem key={index}>
                <HoursDay>{hours.day}</HoursDay>
                <HoursTime>{hours.time}</HoursTime>
              </HoursItem>
            ))}
          </HoursList>
        </SupportHours>
      </BentoGridItem>
    </SupportContainer>
  );
};

export default SupportContactCard;